import { action, observable, autorun, computed } from 'mobx';
import axios from 'axios';

import { numberLockSessionStore } from '~/common/stores/domain/numberLockSession.js';
import preselectedNumberObject from '~/common/utils/preselectedNumberObject.js';
import verifyPreselectedNumber from '~/common/utils/verifyPreselectedNumber.js';
import { ghPreselectedNumberType, ghArea, ghLocalNumberIntent, ghCity } from '~/common/globals.js';
import { isBoolean, isArray, isNumber } from '~/common/utils/typeCheckers';
import { AREA_CODE_NO_RESULTS_ERROR } from '~/common/utils/errorMessaging';

// import other stores
import { lockNumberStore } from '~/common/stores/domain/lockNumber.js';
import { isAreaCodePremium } from '~/common/utils/premiumAreaCodes.js';
import { formsStore } from '~/common/stores/ui/forms';
import { offerSuiteStore } from '~/common/stores/domain/offerSuite';

class LocalNumbersClass {
    @observable localNumbersFetching = false;
    // did the box error
    @observable localNumberError = false;
    // numbers returned from the API, for selection
    @observable localNumbers = [];
    // currently selected area code for local recomended preselected
    @observable selectedAreaCode = '';
    // pre selected number
    @observable preSelectedNumberIndex = 0;
    // if a user has ever searched
    @observable initialSearch = false;
    // search field value
    @observable query = '';
    // holder for previous search (used in error message)
    @observable previousAreaCode = '';
    @observable preselectedLocal = false;

    bootstrapStore() {
        autorun((reaction) => {
            if (decodeURI(ghPreselectedNumberType()).toLowerCase() === 'local') {
                // set preselected number if available
                this.setPreselectedNumber();
                reaction.dispose();
            }
        });

        autorun((reaction) => {
            // need 2 separate query strings because of marketing site needs. ghArea is for preselected number
            if ((ghLocalNumberIntent() && ghLocalNumberIntent().toLowerCase() === 'true') ||
                (ghCity() && ghCity().length > 0) ||
                ghArea()
            ) {
                // if preselected area code save it
                if (ghArea()) this.selectedAreaCode = ghArea();

                this.setPreselectedLocal(true);
                reaction.dispose();
            }
        });
    }

    @action setPreselectedNumber() {
        if (verifyPreselectedNumber('local')) {
            // set flag that the number was preselected on the marketing site
            lockNumberStore.setIsNumberPreselected(true);
            // set selected number with number user preselected
            lockNumberStore.setSelectedNumber(preselectedNumberObject());
            // set local area code
            this.selectedAreaCode = ghArea();
            // set locked number type
            lockNumberStore.setSelectedNumberType('Local');
            // lock number to session
            lockNumberStore.lockNumberToSession();
        }
    }

    @action setPreselectedLocal(bool) {
        this.preselectedLocal = isBoolean(bool, this.preselectedLocal);
    }

    // set selected numbers fetch
    @action setLocalNumbersFetching(bool) {
        this.localNumbersFetching = isBoolean(bool, this.localNumbersFetching);
    }

    // set selected number error
    @action setLocalNumberError(bool) {
        this.localNumberError = isBoolean(bool, this.localNumberError);
    }

    @action setSelectedAreaCode(areaCode) {
        // if area type is string and has a length of 3
        if (typeof areaCode === 'string' && areaCode.length === 3) {
            this.previousAreaCode = this.selectedAreaCode;
            this.selectedAreaCode = areaCode;
        }
    }

    @action resetSelectedAreaCode() {
        this.selectedAreaCode = '';
    }

    @action resetLocalNumbers() {
        this.localNumbers = [];
    }

    // set pre selected number index
    @action setPreSelectedNumberIndex(index) {
        this.preSelectedNumberIndex = isNumber(index, this.preSelectedNumberIndex);
    }

    // set pre selected number index
    @action setInitialSearch(bool) {
        this.initialSearch = isBoolean(bool, this.initialSearch);
    }

    @action updateSearch(q) {
        if (typeof q === 'string' && q.length < 4) {
            this.query = q;
        }
    }

    // get a list of available numbers
    @action getLocalNumbersByNpa(areaCode, callBack, count = 10) {
        // if areaCode exists and is a string
        const regex = new RegExp('^[0-9]{3}$');
        if (typeof areaCode === 'string' && regex.test(parseInt(areaCode, 10))) {
            // reset error flag
            this.setLocalNumbersFetching(true);
            this.setLocalNumberError(false);
            // set area code
            this.setSelectedAreaCode(areaCode);
            // get a list of available area codes based on country and state
            axios.get(`/api/LocalNumberCatalog/LocalNumbersbynpa?areaCode=${areaCode}&count=${count}&numberLockSessionId=${numberLockSessionStore.NLS}${offerSuiteStore.selectedOfferId ? `&offerId=${offerSuiteStore.selectedOfferId}` : ''}`)
                .then((response) => {
                    // no data
                    if (response.data.length === 0) throw new Error('no results from API');
                    // set available area codes
                    this.setLocalNumbers(response.data);
                    if (callBack && this.localNumbers && this.localNumbers.length > 0) callBack(this.localNumbers[0]);
                }).catch((err) => {
                    this.setLocalNumberError(true);
                    formsStore.setFieldError('LocalAreaCode', AREA_CODE_NO_RESULTS_ERROR);

                    return err;
                }).then(() => {
                    this.setLocalNumbersFetching(false);
                });
        } else {
            this.setLocalNumberError(true);
        }
    }

    // set local numbers
    @action setLocalNumbers(data) {
        this.localNumbers = isArray(data, this.localNumbers);
    }

    // if selected area code is premium
    @computed get isSelectedAreaCodePremium() {
        if (!this.selectedAreaCode) return false;

        return isAreaCodePremium(this.selectedAreaCode);
    }

    @computed get selectedNumber() {
        if (this.localNumbers.length) {
            return this.localNumbers[this.preSelectedNumberIndex];
        }
        // return empty object
        return {};
    }

    @computed get fetchedLocalNumbers() {
        return this.localNumbers && this.localNumbers.length > 0;
    }
}

const localNumbersStore = new LocalNumbersClass();
export { localNumbersStore, LocalNumbersClass };
